import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFacebookF, 
  faInstagram, 
  faTwitter, 
  faWhatsapp,
  faYoutube,
  faTiktok 
} from '@fortawesome/free-brands-svg-icons';
import { 
  faEnvelope, 
  faPhone, 
  faMapMarkerAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';
import { PRODUCT_CATEGORIES } from '../../utils/types';
import { Container } from '../../styles/components';

const FooterContainer = styled.footer`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.background};
  padding: ${theme.spacing.xxl} 0 0;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.colors.gradient.gold};
    z-index: 1;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.5fr;
  gap: ${theme.spacing.xl};
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.lg};
    font-size: 1.2rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    display: inline-block;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      width: 30px;
      height: 2px;
      background-color: ${theme.colors.primary};
      bottom: -8px;
      left: 0;
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    margin-bottom: ${theme.spacing.sm};
    transition: transform 0.2s;
    
    &:hover {
      transform: translateX(5px);
    }
  }
  
  a {
    color: ${theme.colors.background};
    text-decoration: none;
    transition: color 0.2s;
    display: inline-flex;
    align-items: center;
    font-size: 0.95rem;
    
    svg {
      opacity: 0;
      transform: translateX(-10px);
      transition: all 0.2s;
      margin-right: ${theme.spacing.xxs};
    }
    
    &:hover {
      color: ${theme.colors.primary};
      
      svg {
        opacity: 1;
        transform: translateX(0);
      }
    }
  }
  
  p {
    margin-bottom: ${theme.spacing.md};
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
  }
`;

const LogoSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  
  h2 {
    font-family: ${theme.fonts.heading};
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: ${theme.spacing.md};
    
    span {
      color: ${theme.colors.primary};
    }
  }
  
  p {
    max-width: 320px;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
  
  svg {
    margin-right: ${theme.spacing.sm};
    color: ${theme.colors.primary};
    font-size: 1rem;
    margin-top: 4px;
  }
  
  .contact-details {
    .contact-label {
      display: block;
      color: ${theme.colors.primary};
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: ${theme.spacing.xxs};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: ${theme.borderRadius.round};
    background-color: rgba(255, 255, 255, 0.1);
    color: ${theme.colors.background};
    font-size: 1rem;
    transition: all 0.3s;
    
    &:hover {
      background-color: ${theme.colors.primary};
      color: ${theme.colors.secondary};
      transform: translateY(-3px);
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  
  a {
    color: ${theme.colors.primary};
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Newsletter = styled.div`
  margin-top: ${theme.spacing.lg};
  
  p {
    margin-bottom: ${theme.spacing.md};
  }
  
  form {
    display: flex;
    background: rgba(255, 255, 255, 0.05);
    border-radius: ${theme.borderRadius.pill};
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: ${theme.transitions.medium};
    
    &:focus-within {
      box-shadow: 0 0 0 2px rgba(255, 215, 0, 0.3);
      border-color: ${theme.colors.primary};
    }
  }
  
  input {
    flex: 1;
    padding: ${theme.spacing.md};
    border: none;
    background: transparent;
    color: ${theme.colors.background};
    font-family: ${theme.fonts.main};
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
    
    &:focus {
      outline: none;
    }
  }
  
  button {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
    border: none;
    padding: ${theme.spacing.sm} ${theme.spacing.lg};
    cursor: pointer;
    font-weight: 600;
    transition: ${theme.transitions.short};
    
    &:hover {
      background-color: #e6c200;
    }
  }
`;

const PaymentMethods = styled.div`
  margin-top: ${theme.spacing.lg};
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  
  .payment-icon {
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-weight: 600;
    font-size: 0.8rem;
    color: ${theme.colors.secondary};
    display: inline-block;
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <LogoSection>
              <h2>Alherz <span>Perfume</span></h2>
              <p>Discover luxury fragrances and gifts at Alherz Perfume. We offer a curated selection of premium perfumes, oud, and gifts.</p>
            </LogoSection>
            
            <SocialLinks>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FontAwesomeIcon icon={faTwitter} />
              </a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <FontAwesomeIcon icon={faYoutube} />
              </a>
              <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <FontAwesomeIcon icon={faTiktok} />
              </a>
            </SocialLinks>
            
            <PaymentMethods>
              <span className="payment-icon">VISA</span>
              <span className="payment-icon">MasterCard</span>
              <span className="payment-icon">AmEx</span>
              <span className="payment-icon">PayPal</span>
              <span className="payment-icon">Apple Pay</span>
            </PaymentMethods>
          </FooterSection>
          
          <FooterSection>
            <h3>Quick Links</h3>
            <ul>
              <li>
                <Link to="/">
                  <FontAwesomeIcon icon={faArrowRight} /> Home
                </Link>
              </li>
              <li>
                <Link to="/about">
                  <FontAwesomeIcon icon={faArrowRight} /> About Us
                </Link>
              </li>
              <li>
                <Link to="/products">
                  <FontAwesomeIcon icon={faArrowRight} /> All Products
                </Link>
              </li>
              <li>
                <Link to="/contact">
                  <FontAwesomeIcon icon={faArrowRight} /> Contact Us
                </Link>
              </li>
              <li>
                <Link to="/faq">
                  <FontAwesomeIcon icon={faArrowRight} /> FAQ
                </Link>
              </li>
              <li>
                <Link to="/terms">
                  <FontAwesomeIcon icon={faArrowRight} /> Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy">
                  <FontAwesomeIcon icon={faArrowRight} /> Privacy Policy
                </Link>
              </li>
            </ul>
          </FooterSection>
          
          <FooterSection>
            <h3>Categories</h3>
            <ul>
              {PRODUCT_CATEGORIES.map((category) => (
                <li key={category.id}>
                  <Link to={`/category/${category.id}`}>
                    <FontAwesomeIcon icon={faArrowRight} /> {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </FooterSection>
          
          <FooterSection>
            <h3>Contact Us</h3>
            <ContactInfo>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <div className="contact-details">
                <span className="contact-label">Address</span>
                <span>Shop 123, Seef Mall, Manama, Bahrain</span>
              </div>
            </ContactInfo>
            <ContactInfo>
              <FontAwesomeIcon icon={faPhone} />
              <div className="contact-details">
                <span className="contact-label">Phone</span>
                <a href="tel:+97312345678">+973 1234 5678</a>
              </div>
            </ContactInfo>
            <ContactInfo>
              <FontAwesomeIcon icon={faEnvelope} />
              <div className="contact-details">
                <span className="contact-label">Email</span>
                <a href="mailto:info@alherzperfume.com">info@alherzperfume.com</a>
              </div>
            </ContactInfo>
            
            <Newsletter>
              <p>Subscribe to our newsletter for exclusive offers and updates</p>
              <form>
                <input type="email" placeholder="Your email address" required />
                <button type="submit">Subscribe</button>
              </form>
            </Newsletter>
          </FooterSection>
        </FooterContent>
      </Container>
      
      <Copyright>
        &copy; {currentYear} Alherz Perfume. All Rights Reserved. Designed with <span style={{ color: '#FFD700' }}>♥</span> in Bahrain
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 